/**
 * GroupedImportDialog — the pick → map → preview → import flow for document
 * types where one spreadsheet row is one *line* and rows sharing a
 * `source-ref` collapse into one document.
 *
 * Invoices and proformas both use it; they differ only in their column
 * vocabulary, copy, and which tRPC procedure receives the batch, all of
 * which arrive via `config` and `runner`. Customers imports are flat (one
 * row = one record) and use the pieces in this folder directly instead.
 */
import { type ReactNode, useMemo, useRef, useState } from "react";
import { Download } from "lucide-react";
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@flint/ui";
import {
  applyMapping,
  autoMapHeaders,
  type ColumnSchema,
  mappingSatisfied,
  templateHeaders,
} from "./column-schema.ts";
import { classifyGroupedRow, groupBySourceRef } from "./classify-row.ts";
import { ColumnMappingStep } from "./column-mapping-step.tsx";
import { FilePickStep } from "./file-pick-step.tsx";
import { ImportResultPanel } from "./import-result-panel.tsx";
import { isWorkbookFile, parseCsv, parseXlsx } from "./parse-file.ts";
import { RowStatusBadge } from "./row-status-badge.tsx";
import { downloadXlsxTemplate, type XlsxTemplateSpec } from "./xlsx-template.ts";
import type { GroupedImportRunner, ImportSummary, PreviewRow } from "./types.ts";

/** V1 sync cap — matches `importInput.rows` max on the wire. */
const MAX_ROWS = 500;

/** Rows rendered in the preview before it truncates. */
const PREVIEW_LIMIT = 100;

/**
 * Everything that varies between one grouped document type and the next.
 * Declare it as a module-level constant so it stays referentially stable
 * across renders.
 */
export interface GroupedImportConfig<THeader> {
  /** Singular document noun, lower-case: "invoice". */
  noun: string;
  /** Plural document noun, lower-case: "invoices". */
  nounPlural: string;
  /** Indefinite article for `noun`: "an" for invoice, "a" for proforma. */
  article: string;
  /** DOM id for the file input — must be unique across mounted dialogs. */
  inputId: string;
  /** Copy under the dialog title. */
  dialogDescription: ReactNode;
  /** Copy under the template links, explaining the worked example. */
  templateHint: ReactNode;
  schema: ColumnSchema;
  /** Worked example rows, comma-joined, one per template row. */
  exampleRows: readonly string[];
  /** Downloaded CSV template file name, including the extension. */
  csvFileName: string;
  /** XLSX template details; headers and examples come from the config. */
  xlsx: Omit<XlsxTemplateSpec, "headers" | "exampleRows">;
  /** Builds the document-level header fields from one mapped row. */
  buildHeader: (raw: Record<string, string>) => THeader;
}

interface GroupedImportDialogProps<THeader> {
  config: GroupedImportConfig<THeader>;
  /** Submits the batch and refreshes the list — owns the tRPC procedure. */
  runner: GroupedImportRunner<THeader>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Fires after a successful import AND its cache invalidation resolve, so
   * callers can navigate somewhere more useful than the surface that
   * mounted the dialog. Optional — without it the dialog just shows the
   * result and waits to be closed.
   */
  onImported?: ((result: ImportSummary) => void) | undefined;
}

export function GroupedImportDialog<THeader>({
  config,
  runner,
  open,
  onOpenChange,
  onImported,
}: GroupedImportDialogProps<THeader>) {
  const [step, setStep] = useState<"pick" | "map" | "preview">("pick");
  const [rawRows, setRawRows] = useState<Array<Record<string, string>>>([]);
  const [fileHeaders, setFileHeaders] = useState<string[]>([]);
  const [columnMap, setColumnMap] = useState<Record<string, string>>({});
  const [rows, setRows] = useState<Array<PreviewRow<THeader>>>([]);
  const [fileName, setFileName] = useState<string>("");
  const [parseError, setParseError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportSummary | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const grouped = useMemo(() => groupBySourceRef(rows), [rows]);
  const validGroupCount = grouped.filter((g) => g.lines.length > 0).length;
  const skippedRowCount = rows.filter((r) => r.status !== "valid").length;
  const headers = templateHeaders(config.schema);

  function reset() {
    setStep("pick");
    setRawRows([]);
    setFileHeaders([]);
    setColumnMap({});
    setRows([]);
    setFileName("");
    setParseError(null);
    setResult(null);
    if (inputRef.current) inputRef.current.value = "";
  }

  function handleClose(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  async function handleFile(file: File) {
    setFileName(file.name);
    setParseError(null);
    try {
      const data = isWorkbookFile(file.name) ? await parseXlsx(file) : await parseCsv(file);
      if (data.length === 0) {
        setParseError(
          `The file has no rows. Add at least one ${config.noun} line under the header row.`,
        );
        return;
      }
      if (data.length > MAX_ROWS) {
        setParseError(`File exceeds the ${MAX_ROWS}-row limit. Split it into smaller files.`);
        return;
      }
      // Detect the file's header set from the first row's keys.
      const detected = Object.keys(data[0] ?? {});
      setFileHeaders(detected);
      setRawRows(data);
      const initialMap = autoMapHeaders(config.schema, detected);
      setColumnMap(initialMap);
      // Skip the mapping step only when every required column resolved AND
      // the file carries nothing we'd have to ask about.
      if (
        mappingSatisfied(config.schema, initialMap) &&
        detected.every((h) => h in config.schema.headerMap)
      ) {
        applyMappingAndPreview(data, initialMap);
      } else {
        setStep("map");
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setParseError(`Could not parse the file: ${msg}`);
    }
  }

  function applyMappingAndPreview(
    data: Array<Record<string, string>>,
    mapping: Record<string, string>,
  ) {
    const remapped = applyMapping(data, mapping);
    setRows(
      remapped.map((raw, i) =>
        classifyGroupedRow(raw, i, {
          noun: config.noun,
          article: config.article,
          buildHeader: config.buildHeader,
        }),
      ),
    );
    setStep("preview");
  }

  async function handleImport() {
    const payload = grouped
      .filter((g) => g.lines.length > 0)
      .map((g) => ({ ...g.header, sourceRef: g.sourceRef, lines: g.lines }));
    if (payload.length === 0) return;
    const res = await runner.run(payload);
    // Skipped tally = server-side skips + client-side rows that never made
    // it into the payload.
    const summary = { imported: res.imported, skipped: res.skipped.length + skippedRowCount };
    setResult(summary);
    await runner.invalidate();
    onImported?.(summary);
  }

  return (
    <DialogRoot open={open} onOpenChange={handleClose}>
      <DialogContent size="lg" className="!max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import {config.nounPlural} from CSV or XLSX</DialogTitle>
          <DialogDescription>{config.dialogDescription}</DialogDescription>
        </DialogHeader>

        {result ? (
          <ImportResultPanel summary={result} skipped={runner.skipped} />
        ) : step === "pick" ? (
          <FilePickStep
            inputId={config.inputId}
            accept=".csv,text/csv,.xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            label="Choose a CSV or XLSX file"
            hint="Header row required. UTF-8 CSV or single-sheet XLSX."
            parseError={parseError}
            inputRef={inputRef}
            onFile={(file) => void handleFile(file)}
          >
            <div className="flex items-center gap-4">
              <a
                href={`data:text/csv;charset=utf-8,${encodeURIComponent(
                  headers.join(",") + "\n" + config.exampleRows.join("\n"),
                )}`}
                download={config.csvFileName}
                className="inline-flex items-center gap-1.5 text-xs text-sky-600 hover:underline"
              >
                <Download size={12} aria-hidden />
                CSV template
              </a>
              <button
                type="button"
                onClick={() =>
                  void downloadXlsxTemplate({
                    ...config.xlsx,
                    headers,
                    exampleRows: config.exampleRows,
                  })
                }
                className="inline-flex items-center gap-1.5 text-xs text-sky-600 hover:underline"
              >
                <Download size={12} aria-hidden />
                XLSX template
              </button>
            </div>
            <p className="text-xs text-muted">{config.templateHint}</p>
          </FilePickStep>
        ) : step === "map" ? (
          <ColumnMappingStep
            schema={config.schema}
            fileName={fileName}
            fileHeaders={fileHeaders}
            columnMap={columnMap}
            onColumnMapChange={setColumnMap}
          />
        ) : (
          <div className="mt-4 space-y-3">
            <div className="text-xs text-muted">
              <strong className="text-ink">{fileName}</strong> · {rows.length} row
              {rows.length === 1 ? "" : "s"} → {validGroupCount}{" "}
              {validGroupCount === 1 ? config.noun : config.nounPlural}
              {skippedRowCount > 0 ? (
                <>
                  {" · "}
                  <span className="text-warn">
                    {skippedRowCount} row{skippedRowCount === 1 ? "" : "s"} to skip
                  </span>
                </>
              ) : null}
            </div>
            <div className="max-h-72 overflow-auto rounded-sm border border-line">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-bg text-muted">
                  <tr>
                    <th className="w-8 px-2 py-1 text-left">#</th>
                    <th className="px-2 py-1 text-left">Source ref</th>
                    <th className="px-2 py-1 text-left">Line</th>
                    <th className="px-2 py-1 text-right">Qty</th>
                    <th className="px-2 py-1 text-right">Rate</th>
                    <th className="px-2 py-1 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, PREVIEW_LIMIT).map((r) => (
                    <tr
                      key={r.index}
                      className={`border-t border-line ${r.status !== "valid" ? "opacity-60" : ""}`}
                    >
                      <td className="px-2 py-1 text-muted">{r.index + 2}</td>
                      <td className="px-2 py-1 font-mono text-xs">{r.raw["source-ref"] || "—"}</td>
                      <td className="px-2 py-1">
                        {r.raw.description || <span className="text-muted">—</span>}
                      </td>
                      <td className="px-2 py-1 text-right">{r.raw.quantity || "—"}</td>
                      <td className="px-2 py-1 text-right">{r.raw["rate-rupees"] || "—"}</td>
                      <td className="px-2 py-1">
                        <RowStatusBadge
                          tone={
                            r.status === "valid"
                              ? "valid"
                              : r.status === "duplicate_source_ref"
                                ? "duplicate"
                                : "invalid"
                          }
                          duplicateLabel="Duplicate ref"
                          message={r.message}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length > PREVIEW_LIMIT ? (
                <p className="px-2 py-1 text-xs text-muted">
                  Showing first {PREVIEW_LIMIT} of {rows.length} rows.
                </p>
              ) : null}
            </div>
          </div>
        )}

        <DialogFooter className="mt-5">
          {result ? (
            <button
              type="button"
              onClick={() => handleClose(false)}
              className="inline-flex h-9 items-center rounded-sm bg-ink px-4 text-sm font-medium text-paper hover:bg-ink-2"
            >
              Done
            </button>
          ) : step === "pick" ? (
            <button
              type="button"
              onClick={() => handleClose(false)}
              className="inline-flex h-9 items-center rounded-sm border border-line bg-paper px-4 text-sm font-medium hover:bg-bg"
            >
              Cancel
            </button>
          ) : step === "map" ? (
            <>
              <button
                type="button"
                onClick={reset}
                className="inline-flex h-9 items-center rounded-sm border border-line bg-paper px-4 text-sm font-medium hover:bg-bg"
              >
                Choose different file
              </button>
              <button
                type="button"
                onClick={() => applyMappingAndPreview(rawRows, columnMap)}
                disabled={!mappingSatisfied(config.schema, columnMap)}
                title={
                  mappingSatisfied(config.schema, columnMap)
                    ? undefined
                    : "Map every required template column before continuing."
                }
                className="inline-flex h-9 items-center rounded-sm bg-ink px-4 text-sm font-medium text-paper hover:bg-ink-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Continue to preview
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={reset}
                disabled={runner.isPending}
                className="inline-flex h-9 items-center rounded-sm border border-line bg-paper px-4 text-sm font-medium hover:bg-bg disabled:cursor-not-allowed disabled:opacity-60"
              >
                Choose different file
              </button>
              <button
                type="button"
                onClick={() => void handleImport()}
                disabled={validGroupCount === 0 || runner.isPending}
                className="inline-flex h-9 items-center rounded-sm bg-ink px-4 text-sm font-medium text-paper hover:bg-ink-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {runner.isPending
                  ? "Importing…"
                  : `Import ${validGroupCount} ${validGroupCount === 1 ? config.noun : config.nounPlural}`}
              </button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
